import { FC, useCallback, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import {
  getScheduledEvent,
  getTasksByScheduledEventId,
  getUserRoles,
} from '@/utils/stateAccessors';

import MiniLoader from '@payaca/components/miniLoader/MiniLoader';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import TaskControl from '../taskControl/TaskControl';

import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import { TasksPermissions } from '@payaca/permissions/tasks/tasks.permissions';

import * as scheduledEventActions from '@payaca/store/scheduledEvents/scheduledEventsActions';
import * as taskActions from '@payaca/store/tasks/tasksActions';

import { useSelector } from '@/api/state';
import CreateEditTaskSidebar from '@/ui/components/createEditTaskSidebar/CreateEditTaskSidebar';
import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';
import { requestGetDocumentForTask } from '@payaca/store/documents/documentActions';
import ScheduledEventTaskStatusBadge from '../scheduledEventTaskStatusBadge/ScheduledEventTaskStatusBadge';
import { SectionDivider, SectionTitle } from './SectionComponents';

type Props = {
  scheduledEventId: number;
  onUpdateTasksSuccess?: () => void;
};
export const TasksSection: FC<Props> = ({
  scheduledEventId,
  onUpdateTasksSuccess,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);

  const userRoles = useSelector(getUserRoles);

  const profile = useSelector((state: any) => state.users.myProfile);

  const scheduledEvent = useSelector((state) =>
    getScheduledEvent(state, scheduledEventId)
  );

  const tasks = useSelector((state) => {
    return getTasksByScheduledEventId(state, scheduledEventId);
  });

  const completeTasks = useMemo(
    () => tasks.filter((task) => !!task.completedAt),
    [tasks]
  );

  const incompleteTasks = useMemo(
    () => tasks.filter((task) => !task.completedAt),
    [tasks]
  );

  const isGettingTasksForScheduledEvent = useSelector((state) => {
    return state.tasks.isGettingTasksForScheduledEvent;
  });

  const retrieveScheduledEvent = useCallback(() => {
    dispatch(scheduledEventActions.requestGetScheduledEvent(scheduledEventId));
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [scheduledEventId]);

  const retrieveTask = useCallback(
    (taskId: number, isTaskDocument: boolean) => {
      dispatch(taskActions.requestGetTask(taskId));
      if (isTaskDocument) {
        dispatch(requestGetDocumentForTask(taskId));
      }
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    []
  );

  const canOnlyViewOwnTasks = useMemo(() => {
    return (
      !userHasRequiredPermission(userRoles, [TasksPermissions.PERSIST_TASK]) &&
      userHasRequiredPermission(userRoles, [
        TasksPermissions.PERSIST_SELF_CREATED_TASK,
      ])
    );
  }, [userRoles]);

  if (!scheduledEvent) return null;

  return (
    <div>
      <div className="flex flex-row items-center gap-4">
        <SectionTitle>Tasks</SectionTitle>
        <ScheduledEventTaskStatusBadge status={scheduledEvent.taskStatus} />
        <PermissionGuard
          renderIfHasPermissions={[
            TasksPermissions.PERSIST_TASK,
            TasksPermissions.PERSIST_SELF_CREATED_TASK,
          ]}
        >
          <>
            <div className="ml-auto">
              <Button
                styleVariant={ButtonStyleVariant.ANCHOR}
                onClick={() => setShowCreateTaskModal(true)}
              >
                Add Task
              </Button>
            </div>
            <CreateEditTaskSidebar
              isOpen={showCreateTaskModal}
              projectId={scheduledEvent.dealId || undefined}
              scheduledEventId={scheduledEventId}
              onClose={() => setShowCreateTaskModal(false)}
              onSuccess={(taskId) => {
                dispatch(
                  scheduledEventActions.requestGetScheduledEvent(
                    scheduledEvent.id
                  )
                );
                dispatch(taskActions.requestGetTask(taskId));
              }}
            />
          </>
        </PermissionGuard>
      </div>
      <SectionDivider />
      {isGettingTasksForScheduledEvent && !tasks?.length ? (
        <div className="flex items-center justify-center">
          <MiniLoader />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {!!incompleteTasks.length && (
            <div className="flex flex-col gap-2">
              {incompleteTasks.map((task, index) => {
                return (
                  <TaskControl
                    taskId={task.id}
                    key={`incomplete-task-control-${index}`}
                    onPersistTaskSuccess={() => {
                      onUpdateTasksSuccess?.();
                      retrieveTask(task.id, !!task.documentId);
                    }}
                    onDeleteTaskSuccess={() => {
                      onUpdateTasksSuccess?.();
                      retrieveScheduledEvent();
                    }}
                    dealId={scheduledEvent.dealId || undefined}
                  />
                );
              })}
            </div>
          )}
          {!!completeTasks.length && (
            <div className="flex flex-col gap-2">
              {completeTasks.map((task, index) => {
                return (
                  <TaskControl
                    taskId={task.id}
                    key={`complete-task-control-${index}`}
                    onPersistTaskSuccess={() => {
                      onUpdateTasksSuccess?.();
                      retrieveTask(task.id, !!task.documentId);
                    }}
                    onDeleteTaskSuccess={() => {
                      onUpdateTasksSuccess?.();
                      retrieveScheduledEvent();
                    }}
                    dealId={scheduledEvent.dealId || undefined}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
